'use client';

import { CaretUp } from 'assets/icons';
import { forwardRef, useRef, useState } from 'react';
import { Container } from '..';
import styles from './HeroCategories.module.css';
import { Carousel } from './Carousel';
import {
  athleteReviews,
  carouselImagesAthletes,
  carouselImagesHealthCare,
  healthcareWorkerReviews,
} from './data';
import { Hero } from './HeroCatSection';
import { Reviews } from './Reviews';
import { ButtonRows, ButtonTabs } from './StyledConstants';
import { useEffect } from 'react';
import { HeroCategoriesMap } from 'components/shared/Cart/utils';
import { useProductContext } from 'hooks/navigation/useProductContext';

function getHeroCategories(buyLink: string, productName: string): {
  [key: string]: { title: string; children?: React.ReactNode };
} {
  return {
  [HeroCategoriesMap.ATHLETES]: {
    title: 'Athletes',
    children: (
      <>
        <Hero
          title="The edge you need for peak performance"
          subTitle={`The ${productName} provides insights into recovery, performance, and endurance, showing how your body adapts to training and flags fatigue or stress.`}
          rootClass="padding"
          img={{
            desktopSrc: 'Athlete_Banner.jpg',
            mobileSrc: 'Athletes_Banner.jpg',
            alt: 'Athletes',
            width: 4808,
            height: 2888,
            caption:
              `Champions are built on recovery and resilience. Ultrahuman ensures that your journey to greatness is supported at every step. Optimize your performance, track your recovery, and improve your training with the ${productName}.`,
            captiionAlignment: 'right',
          }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'athletes',
          }}
        />
        <Hero
          title="Powering world champions"
          subTitle={`Elite athletes trust the ${productName} to track sleep, movement, and recovery, ensuring peak performance throughout training and competition.`}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'athletes',
          }}
        />
        <Carousel carouselImages={carouselImagesAthletes} />
        <Reviews reviews={athleteReviews} />
      </>
    ),
  },
  [HeroCategoriesMap.HEALTHCARE]: {
    title: 'Healthcare Workers',
    children: (
      <>
        <Hero
          title="For those who care for us all"
          subTitle={`Healthcare workers endure long shifts that spike stress and burnout risk. The ${productName} tracks vital metrics to support overall well-being.`}
          rootClass="padding"
          img={{
            desktopSrc: 'Healthcare_Banner_latest.jpg',
            mobileSrc: 'Healthcare_Banner.jpg',
            alt: 'healthcare',
            width: 4808,
            height: 2888,
            caption:
              'Access science-backed tools to boost recovery and self-care. Join a community of healthcare workers using Ultrahuman’s technology to stay at the top of their game so you can continue providing the best care while taking care of yourself.',
            captiionAlignment: 'right',
          }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'healthcare-workers',
          }}
        />
        <Hero
          title="Supporting Nurse Wellness"
          subTitle={`By leveraging metrics like sleep, HRV, and skin temperature, the ${productName} helps healthcare workers to better manage their overall well-being.`}
          // img={{
          //   desktopSrc: 'Supporting-Nurse-Wellness.jpg',
          //   mobileSrc: 'Supporting-Nurse-Wellness.jpg',
          //   alt: 'Veterans',
          //   width: 4808,
          //   height: 2888,
          //   caption:
          //     'The Ultrahuman Ring AIR is more than a health tracker—it’s a comprehensive tool for empowering healthcare professionals to manage their health proactively. By leveraging metrics like sleep quality, HRV, and skin temperature, the Ring AIR enables first responders to better understand and manage the physical and mental challenges they face.',
          //   captiionAlignment: 'left',
          // }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'healthcare-workers',
          }}
        />
        <Carousel carouselImages={carouselImagesHealthCare} />
        <Reviews reviews={healthcareWorkerReviews} />{' '}
      </>
    ),
  },
  [HeroCategoriesMap.SCIENTIST]: {
    title: 'Scientists & Researchers',
    children: (
      <>
        <Hero
          title="Health insights that power innovation"
          subTitle={`Comprehensive, objective health data fuels discovery. The ${productName} tracks key metrics in real time, offering precise data to drive next-gen research.`}
          rootClass="padding"
          img={{
            desktopSrc: 'Science_Banner.jpg',
            mobileSrc: 'Science_Banner.jpg',
            alt: 'Scientist & researchers',
            width: 4808,
            height: 2888,
            caption:
              `Designed for those pushing the boundaries of knowledge, the ${productName} also provides deep physiological insights to help you sustain focus, enhance recovery, and perform at your best.`,
            captiionAlignment: 'right',
          }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',

            heroCategory: 'scientist-and-researchers',
          }}
        />
        <Hero
          title="Introducing UltraSignal"
          subTitle="Access PPG, accelerometer, and temperature data streams to build custom health algorithms. Refine protocols and uncover novel insights with UltraSignal."
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'scientist-and-researchers',
          }}
          vid={{
            src: 'web_v2/hero/hero-categories/desktop/Scientists-Researchers-Banner.mp4',
            type: 'video/mp4',
          }}
        />
      </>
    ),
  },
  [HeroCategoriesMap.VETERANS]: {
    title: 'Veterans & Military',
    children: (
      <>
        <Hero
          title="Thank you for your service. Today, let us serve you"
          subTitle={`Your efforts safeguard countless lives every day and we deeply appreciate your sacrifices. As a special thank you, get exclusive savings on your ${productName}.`}
          rootClass="padding"
          img={{
            desktopSrc: 'Veterans_Banner.jpg',
            mobileSrc: 'Veterans_Banner.jpg',
            alt: 'Veterans',
            width: 4808,
            height: 2888,
            caption:
              `The ${productName}'s sleek design offers superior comfort while monitoring your sleep, movement, heart rate, HRV, and skin temperature. Access insights to maintain performance, both in daily life and beyond the call of duty.`,
            captiionAlignment: 'right',
          }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'veterans',
          }}
        />
      </>
    ),
  },

  [HeroCategoriesMap.EDUCATION]: {
    title: 'Education',
    children: (
      <>
        <Hero
          title="Powering the pursuit of knowledge"
          subTitle="From classrooms to study halls, stress and poor sleep can hinder performance—for both educators and students. Prioritize rest and recovery to stay focused and resilient."
          rootClass="padding"
          img={{
            desktopSrc: 'Students_Banner.jpg',
            mobileSrc: 'Students_Banner.jpg',
            alt: 'Education',
            width: 4808,
            height: 2888,
            caption:
              `Discover the endless potential of the ${productName}. Track your health, prioritise rest, and manage stress through the demands of teaching, learning, and everything in between. Use advanced insights to stay balanced and perform at your best.`,
            captiionAlignment: 'right',
          }}
          cta={{
            link: buyLink,
            text: 'Get special pricing',
            heroCategory: 'education',
          }}
        />
      </>
    ),
  },
  };
}
// eslint-disable-next-line react/display-name
export const HeroCategories = forwardRef(
  ({ className }: { className?: string }, ref: React.Ref<HTMLElement>) => {
      const { buyLink, productName } = useProductContext();
      const heroCategories = getHeroCategories(buyLink, productName);

      const [active, setActive] = useState(
        () => Object.keys(heroCategories)[0],
      );
      const [showLeftCaret, setShowLeftCaret] = useState(false);
      const [showRightCaret, setShowRightCaret] = useState(false);
      const containerRef = useRef<HTMLDivElement>(null);
      const buttonRowsRef = useRef<HTMLDivElement>(null);

      const checkOverflow = () => {
        if (buttonRowsRef.current) {
          const { scrollWidth, clientWidth, scrollLeft } =
            buttonRowsRef.current;
          setShowLeftCaret(scrollLeft > 0);
          setShowRightCaret(
            scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth,
          );
        }
      };

      useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
      }, []);

      const scroll = (direction: 'left' | 'right') => {
        if (buttonRowsRef.current) {
          const scrollAmount = 200;
          buttonRowsRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
          });
        }
      };

      const handleScroll = () => {
        checkOverflow();
      };

      return (
        <section
          ref={ref}
          className={`${styles.root}${className ? ` ${className}` : ''}`}
        >
          <Container ref={containerRef}>
            <div className={styles.buttonRowsWrapper}>
              {showLeftCaret && (
                <button
                  className={`${styles.scrollButton} left`}
                  onClick={() => scroll('left')}
                  aria-label="Scroll left"
                >
                  <CaretUp />
                </button>
              )}
              <ButtonRows ref={buttonRowsRef} onScroll={handleScroll}>
                {Object.keys(heroCategories).map((key) => (
                  <ButtonTabs
                    className={key === active ? 'active' : ''}
                    key={key}
                    onClick={(e) => {
                      setActive(key);
                      if (window.innerWidth > 769) return;
                      (e.target as HTMLButtonElement).scrollIntoView({
                        behavior: 'smooth',
                        inline: 'center',
                        block: 'start',
                      });
                    }}
                  >
                    {heroCategories[key].title}
                  </ButtonTabs>
                ))}
              </ButtonRows>
              {showRightCaret && (
                <button
                  className={`${styles.scrollButton} right`}
                  onClick={() => scroll('right')}
                  aria-label="Scroll right"
                >
                  <CaretUp />
                </button>
              )}
            </div>
            <div className={styles.content}>
              {heroCategories[active].children}
            </div>
          </Container>
        </section>
      );
    },
);
